import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import { Plug, Play, RefreshCw, Loader2, AlertCircle, CheckCircle2, Eye, Save, FileSpreadsheet, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { admin } from '@/lib/api';

const formatTRY = (value) => {
  const n = Number(value);
  if (value === null || value === undefined || Number.isNaN(n)) return '—';
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
};

const IntegrationsTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [status, setStatus] = useState(null);
  const [settingsMeta, setSettingsMeta] = useState(null);
  const [lastRunResult, setLastRunResult] = useState(null);

  const [formProductsUrl, setFormProductsUrl] = useState('');
  const [formApiKey, setFormApiKey] = useState('');
  const [formApiBase, setFormApiBase] = useState('');
  const [formMarkupPct, setFormMarkupPct] = useState('0');
  const [pricingPreview, setPricingPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailDoc, setDetailDoc] = useState(null);
  const [listingToggleLoading, setListingToggleLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [parasut, setParasut] = useState(null);
  const [parasutLoading, setParasutLoading] = useState(true);
  const [parasutBusy, setParasutBusy] = useState(false);

  const loadParasut = useCallback(async () => {
    setParasutLoading(true);
    try {
      const { data } = await admin.getParasutIntegrationStatus();
      setParasut(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Paraşüt durumu alınamadı');
    } finally {
      setParasutLoading(false);
    }
  }, []);

  const loadAll = useCallback(async (opts = {}) => {
    const quiet = opts.quiet === true;
    if (!quiet) setLoading(true);
    try {
      const [st, sett] = await Promise.all([
        admin.lastikcimIntegrationStatus(),
        admin.getLastikcimIntegrationSettings(),
      ]);
      setStatus(st.data);
      setSettingsMeta(sett.data);
      if (sett.data?.api_base_saved) {
        setFormApiBase(sett.data.api_base_saved);
      }
      setFormMarkupPct(
        sett.data?.price_markup_percent != null ? String(Number(sett.data.price_markup_percent)) : '0'
      );
      setPricingPreview(null);
      if (st.data?.configured && !quiet) {
        try {
          const pr = await admin.previewLastikcimPricing({});
          setPricingPreview(pr.data);
        } catch {
          /* önizleme isteğe bağlı */
        }
      }
      return st.data;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Yüklenemedi');
      return null;
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []);

  const handlePreviewPricing = async () => {
    if (!status?.configured) {
      toast.error('Önce API yapılandırması tamamlansın.');
      return;
    }
    setPreviewLoading(true);
    try {
      const raw = Number.parseFloat(String(formMarkupPct).replace(',', '.'));
      const pct = Number.isFinite(raw) ? raw : 0;
      const { data } = await admin.previewLastikcimPricing({ markup_percent: pct });
      setPricingPreview(data);
      if (!data?.ok && (data?.message || data?.error)) {
        toast.error(data?.message || data?.error || 'Önizleme başarısız');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Önizleme başarısız');
      setPricingPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    loadParasut();
  }, [loadAll, loadParasut]);

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const flag = q.get('parasut');
    if (flag === 'connected') {
      toast.success('Paraşüt hesabı bağlandı. Sipariş faturaları bu oturumla gidecek.');
    } else if (flag === 'error') {
      toast.error(q.get('reason') || 'Paraşüt bağlantısı kurulamadı');
    }
  }, []);

  const handleParasutConnect = async () => {
    setParasutBusy(true);
    try {
      const { data } = await admin.startParasutConnect();
      if (!data?.authorize_url) {
        toast.error('Paraşüt yetkilendirme adresi gelmedi');
        setParasutBusy(false);
        return;
      }
      window.location.assign(data.authorize_url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Paraşüt bağlantısı başlatılamadı');
      setParasutBusy(false);
    }
  };

  const handleParasutDisconnect = async () => {
    if (!window.confirm('Paraşüt bağlantısı kesilsin mi? Yeni faturalar, hesabı yeniden bağlayana kadar gitmez.')) {
      return;
    }
    setParasutBusy(true);
    try {
      await admin.disconnectParasut();
      toast.success('Paraşüt bağlantısı kesildi');
      await loadParasut();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bağlantı kesilemedi');
    } finally {
      setParasutBusy(false);
    }
  };

  const copyRedirectUri = async () => {
    const uri = parasut?.redirect_uri;
    if (!uri) return;
    try {
      await navigator.clipboard.writeText(uri);
      toast.success('Geri dönüş adresi kopyalandı');
    } catch {
      toast.error('Kopyalanamadı');
    }
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      const pct = Number.parseFloat(String(formMarkupPct).replace(',', '.'));
      const payload = {
        products_url: formProductsUrl.trim(),
        api_base: formApiBase.trim(),
        price_markup_percent: Number.isFinite(pct) ? pct : 0,
      };
      if (formApiKey.trim()) {
        payload.api_key = formApiKey.trim();
      }
      const { data } = await admin.updateLastikcimIntegrationSettings(payload);
      setFormApiKey('');
      toast.success(data?.message || 'Kaydedildi');
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const handleListingEnabledChange = async (checked) => {
    setListingToggleLoading(true);
    try {
      const { data } = await admin.updateLastikcimListingEnabled({ listing_enabled: checked });
      const n = data?.products_modified ?? 0;
      toast.success(
        checked
          ? `Vitrin açık. ${n} Lastikçim ürünü güncellendi.`
          : `Vitrin kapalı (pasif). ${n} Lastikçim ürünü güncellendi.`
      );
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Vitrin ayarı güncellenemedi');
    } finally {
      setListingToggleLoading(false);
    }
  };

  const handleRun = async () => {
    setRunning(true);
    setLastRunResult(null);
    const prevHeadId = status?.recent_runs?.[0]?.id ?? null;
    try {
      const res = await admin.lastikcimIntegrationRun();
      const { data, status: httpStatus } = res;

      if (httpStatus === 202 || data?.accepted) {
        toast.info(
          data?.message ||
            'İçe aktarma arka planda başladı. Birkaç dakika sürebilir — «Son çalıştırmalar» güncellenecek.'
        );
        let sawNew = false;
        for (let i = 0; i < 120; i += 1) {
          await new Promise((r) => setTimeout(r, 3000));
          const st = await loadAll({ quiet: true });
          if (!st?.recent_runs?.length) continue;
          const head = st.recent_runs[0];
          const headId = head?.id ?? null;
          const resObj = head?.result;
          const isNewHead = headId != null && headId !== prevHeadId;
          if (isNewHead && resObj != null) {
            sawNew = true;
            setLastRunResult(resObj);
            if (resObj.ok) {
              toast.success(
                `Tamam: +${resObj.imported ?? 0} yeni, ${resObj.updated ?? 0} güncellendi (${resObj.duration_ms ?? '—'} ms)`
              );
            } else {
              toast.error(resObj.message || resObj.error || 'İçe aktarma tamamlandı ancak hata özeti var.');
            }
            break;
          }
        }
        if (!sawNew) {
          toast.warning(
            'İşlem zaman aşımına yaklaştı veya hâlâ sürüyor. «Yenile»ye basıp son çalıştırma kaydını açın.'
          );
        }
      } else if (data?.ok) {
        setLastRunResult(data);
        toast.success(
          `Tamam: +${data.imported} yeni, ${data.updated} güncellendi (${data.duration_ms} ms)`
        );
      }
      await loadAll({ quiet: false });
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Çalıştırma hatası';
      toast.error(typeof msg === 'string' ? msg : 'Çalıştırma hatası');
    } finally {
      setRunning(false);
    }
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const res = await admin.exportLastikcimProductsExcel();
      const blob =
        res.data instanceof Blob
          ? res.data
          : new Blob([res.data], {
              type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
      let filename = `lastikcim_urunler_${Date.now()}.xlsx`;
      const cd = res.headers?.['content-disposition'] || res.headers?.['Content-Disposition'];
      if (cd && typeof cd === 'string') {
        const star = cd.match(/filename\*=UTF-8''([^;\s]+)/i);
        if (star?.[1]) filename = decodeURIComponent(star[1]);
        else {
          const q = cd.match(/filename="([^"]+)"/i);
          if (q?.[1]) filename = q[1];
        }
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      const rawHdr = res.headers?.['x-lastikcim-export-rows'];
      const rowN = rawHdr != null ? Number.parseInt(String(rawHdr), 10) : NaN;
      const rowSuffix = Number.isFinite(rowN) ? ` (${rowN} ürün satırı)` : '';
      toast.success(`Excel hazır${rowSuffix}. Dosya adı ile indirilen satır sayısı aynı olmalıdır.`);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'İndirilemedi';
      toast.error(typeof msg === 'string' ? msg : 'İndirilemedi');
    } finally {
      setExporting(false);
    }
  };

  const openDetail = async (runId) => {
    if (!runId) return;
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailDoc(null);
    try {
      const { data } = await admin.lastikcimRunDetail(runId);
      setDetailDoc(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Detay yüklenemedi');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const recent = status?.recent_runs || [];
  const source = status?.config_source || 'none';

  return (
    <div className="max-w-5xl mx-auto space-y-8">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-slate-900 flex items-center justify-center">
          <Plug className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Entegrasyonlar</h1>
          <p className="text-slate-600 text-sm">
            Dış tedarikçi API bağlantıları — panelden kayıt veya sunucu <code className="text-xs">.env</code>.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <CardTitle className="flex flex-wrap items-center gap-2">
                Paraşüt
                {parasutLoading ? (
                  <Badge variant="secondary">Yükleniyor…</Badge>
                ) : parasut?.connected ? (
                  <Badge className="bg-emerald-600 hover:bg-emerald-600">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    Bağlı
                  </Badge>
                ) : parasut?.password_fallback ? (
                  <Badge variant="outline" className="border-amber-400 text-amber-800">
                    Eski şifre yöntemi
                  </Badge>
                ) : (
                  <Badge variant="destructive">
                    <AlertCircle className="w-3 h-3 mr-1" />
                    Bağlı değil
                  </Badge>
                )}
              </CardTitle>
              <CardDescription className="mt-2 max-w-2xl space-y-2">
                <span className="block">
                  10 Aralık 2026’da e-posta ve şifre ile token alma kapanıyor. DevHub’dan yeni istemci alıp
                  aşağıdaki adresle bir kez yetkilendirin. Refresh token sunucuda saklanır.
                </span>
                <span className="block">{parasut?.permission_hint}</span>
              </CardDescription>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={loadParasut} disabled={parasutLoading || parasutBusy}>
                <RefreshCw className={`w-4 h-4 mr-2 ${parasutLoading ? 'animate-spin' : ''}`} />
                Yenile
              </Button>
              <Button size="sm" onClick={handleParasutConnect} disabled={parasutLoading || parasutBusy}>
                {parasutBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {parasut?.connected ? 'Yeniden bağla' : 'Paraşüt hesabını bağla'}
              </Button>
              {parasut?.connected && (
                <Button variant="outline" size="sm" onClick={handleParasutDisconnect} disabled={parasutBusy}>
                  Bağlantıyı kes
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-slate-700">
          {parasut?.needs_reconnect && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {parasut.last_error || 'Oturum iptal edilmiş. Paraşüt hesabını yeniden bağlayın.'}
              </AlertDescription>
            </Alert>
          )}
          <div className="grid gap-2 sm:grid-cols-2">
            <div>Firma no: <strong>{parasut?.company_id || '—'}</strong></div>
            <div>İstemci: <strong>{parasut?.client_id_hint || 'tanımsız'}</strong></div>
            <div>Bağlanma: <strong>{parasut?.connected_at ? new Date(parasut.connected_at).toLocaleString('tr-TR') : '—'}</strong></div>
            <div>Refresh bitiş: <strong>{parasut?.refresh_expires_at ? new Date(parasut.refresh_expires_at).toLocaleString('tr-TR') : '—'}</strong></div>
          </div>
          <div className="space-y-1">
            <Label>DevHub geri dönüş adresi</Label>
            <div className="flex gap-2">
              <Input readOnly value={parasut?.redirect_uri || ''} />
              <Button type="button" variant="outline" size="icon" onClick={copyRedirectUri} title="Kopyala">
                <Copy className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs text-slate-500">
              Başvuru: {parasut?.devhub_url || 'https://devhub.parasut.com/api-basvuru'} — «Paraşüt Kullanıcısıyım».
              Uygulama adı: Turkish E-Market.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <CardTitle className="flex flex-wrap items-center gap-2">
                Lastikçim B2B
                {loading ? (
                  <Badge variant="secondary">Yükleniyor…</Badge>
                ) : status?.configured ? (
                  <Badge className="bg-emerald-600 hover:bg-emerald-600">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    Yapılandırılmış
                  </Badge>
                ) : (
                  <Badge variant="destructive">
                    <AlertCircle className="w-3 h-3 mr-1" />
                    Eksik yapılandırma
                  </Badge>
                )}
                {!loading && status?.configured && (
                  <Badge variant="outline" className="font-normal">
                    Kaynak: {source === 'environment' ? 'Ortam (.env)' : source === 'database' ? 'Veritabanı' : '—'}
                  </Badge>
                )}
              </CardTitle>
              <CardDescription className="mt-2 max-w-2xl space-y-1">
                <span className="block">
                  Öncelik: <strong>önce</strong>{' '}
                  <code className="text-xs bg-slate-100 px-1 rounded">LASTIKCIM_PRODUCTS_URL</code> /{' '}
                  <code className="text-xs bg-slate-100 px-1 rounded">LASTIKCIM_API_KEY</code> (sunucu ortamı),{' '}
                  <strong>yoksa</strong> aşağıda kaydettiğiniz değerler kullanılır.
                </span>
                <span className="block mt-2 text-amber-900/90">
                  Excel’de satır az görünüyorsa bu genelde{' '}
                  <strong>yanlış değildir:</strong> dosya doğrudan <strong>Mongo’daki kayıtları</strong> yazar.
                  Katalog daha büyükse önce{' '}
                  <strong className="text-slate-900">Çalıştır</strong> ile içe aktarın; güncel backend, parametresiz
                  API’nin ilk sayfasını (~50 satır gibi) bırakmadan mümkün olan sayfa/limit parametreleriyle{' '}
                  <strong>tüm kimlikleri</strong> çekip veritabanını günceller.
                </span>
              </CardDescription>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={loadAll}
                disabled={loading || running || listingToggleLoading}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Yenile
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportExcel}
                disabled={loading || running || listingToggleLoading || exporting}
                title="source=lastikcim veya id prod-lastikcim-… — veritabanındaki tüm entegrasyon ürünleri"
              >
                {exporting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    İndiriliyor…
                  </>
                ) : (
                  <>
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    Excel indir
                  </>
                )}
              </Button>
              <Button
                size="sm"
                onClick={handleRun}
                disabled={loading || running || listingToggleLoading || !status?.configured}
              >
                {running ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Çalışıyor…
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 mr-2" />
                    Çalıştır
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {!loading && !status?.configured && (status?.recent_runs || []).length > 0 && (
            <Alert className="border-amber-300 bg-amber-50">
              <AlertCircle className="h-4 w-4 text-amber-800" />
              <AlertDescription className="text-amber-900">
                Kayıtta başarılı çalıştırmalar görünüyor ama <strong>şu an</strong> ne sunucu ortamında ne de veritabanında
                geçerli API adresi görünmüyor (ör. .env taşınmadı/kaldırıldı ya da henüz <strong>Kaydet</strong> yapılmadı).
                Aşağıdan tam URL veya anahtarı kaydedin; aksi halde <strong>Çalıştır</strong> bu ortamda hata verir.
              </AlertDescription>
            </Alert>
          )}

          {!loading &&
            status?.lastikcim_products_total > 0 &&
            status?.lastikcim_products_active === 0 &&
            settingsMeta?.listing_enabled !== false && (
              <Alert className="border-amber-300 bg-amber-50">
                <AlertCircle className="h-4 w-4 text-amber-800" />
                <AlertDescription className="text-amber-900">
                  {status.lastikcim_products_total} Lastikçim ürünü veritabanında ama{' '}
                  <strong>hiçbiri mağaza listesinde çıkmıyor</strong> (<code className="text-xs">is_active</code>{' '}
                  hep kapalı olabilir). Bir kez şu anahtarı <strong>kapat-aç</strong> deneyin veya Admin ürün listesinden
                  durumu kontrol edin.
                </AlertDescription>
              </Alert>
            )}

          {!loading && settingsMeta?.listing_enabled === false && status?.lastikcim_products_total > 0 && (
            <Alert>
              <AlertDescription>
                <strong>Vitrin kapalı:</strong> “Lastikçim ürünlerini vitrine çıkar” kapalı olduğu için{' '}
                <code className="text-xs">source: lastikcim</code> veya{' '}
                <code className="text-xs">prod-lastikcim-… id</code> ürünleri{' '}
                <code className="text-xs">is_active: false</code> — ana sayfa ve{' '}
                <code className="text-xs">/products</code> sorgusu bunları göstermez. Ürünleri sitede görmek için anahtarı{' '}
                <strong>açın</strong> (otomatik toplu güncelleme yapılır).
              </AlertDescription>
            </Alert>
          )}

          {!loading &&
            (status?.lastikcim_products_total != null ||
              status?.lastikcim_products_active != null) && (
              <p className="text-sm text-slate-700">
                <span className="font-medium text-slate-900">Mağaza listesi:</span> veritabanında{' '}
                <strong>{status.lastikcim_products_total ?? 0}</strong> Lastikçim ürünü; bunlardan{' '}
                <strong>{status.lastikcim_products_active ?? 0}</strong> tanesi vitrin için{' '}
                <strong>aktif</strong> (<code className="text-xs">is_active: true</code>). Aynı kapsam Excel
                indirmede de kullanılır: <code className="text-xs">source: lastikcim</code> veya{' '}
                <code className="text-xs">id</code> öneki <code className="text-xs">prod-lastikcim-</code>.
              </p>
            )}

          {settingsMeta?.env_overrides_database && (
            <Alert>
              <AlertDescription>
                Şu an geçerli yapılandırma <strong>ortam değişkeninden</strong> geliyor. Panelde kayıtlı
                değerler yedek olarak durur; .env kaldırıldığında veritabanı devreye girer.
              </AlertDescription>
            </Alert>
          )}

          {settingsMeta?.price_markup_env_overrides_db && (
            <Alert>
              <AlertDescription>
                Ek satış yüzdesi <strong>sunucuda</strong>{' '}
                <code className="text-xs bg-slate-100 px-1 rounded">LASTIKCIM_PRICE_MARKUP_PERCENT</code> ile ayarlı;
                panel yüzdesi bu ortamda yalnız bilgi için veritabanına yazılır — hesaplamada ortam önceliklidir.
              </AlertDescription>
            </Alert>
          )}

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 rounded-lg border border-slate-200 bg-white p-4">
            <div className="space-y-1 max-w-xl">
              <Label htmlFor="lc-listing" className="text-sm font-semibold text-slate-800">
                Lastikçim ürünlerini vitrine çıkar
              </Label>
              <p id="lc-listing-hint" className="text-xs text-slate-600">
                Kapattığınızda yalnızca <code className="text-[11px] bg-slate-100 px-1 rounded">source: lastikcim</code>{' '}
                veya <code className="text-[11px] bg-slate-100 px-1 rounded">id</code> si{' '}
                <code className="text-[11px] bg-slate-100 px-1 rounded">prod-lastikcim-</code> ile başlayan ürünler
                pasif olur; diğer satıcılar etkilenmez. Açıkken içe aktarılan satırlar da vitrine uygun{' '}
                <code className="text-[11px] bg-slate-100 px-1 rounded">is_active</code> alır.
              </p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {listingToggleLoading && <Loader2 className="w-4 h-4 animate-spin text-slate-500" />}
              <Switch
                id="lc-listing"
                checked={settingsMeta?.listing_enabled !== false}
                disabled={loading || listingToggleLoading}
                onCheckedChange={handleListingEnabledChange}
                aria-describedby="lc-listing-hint"
              />
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-slate-50/80 p-4 space-y-4">
            <h3 className="text-sm font-semibold text-slate-800">Panel üzerinden kaydet</h3>
            <p className="text-xs text-slate-600">
              {settingsMeta?.has_saved_products_url && (
                <span className="block mb-1">Tam API adresi veritabanında kayıtlı (güvenlik için metin kutusu boş gösterilir).</span>
              )}
              {settingsMeta?.has_saved_api_key && (
                <span className="block mb-1">API anahtarı kayıtlı. Değiştirmek için yeni anahtarı yazın.</span>
              )}
            </p>
            <div className="space-y-2">
              <Label htmlFor="lc-url">Tam ürün API URL (key= içerebilir)</Label>
              <Input
                id="lc-url"
                type="url"
                autoComplete="off"
                placeholder="http://yonetim.lastikcim.com.tr/api/products/turkishemarket?key=..."
                value={formProductsUrl}
                onChange={(e) => setFormProductsUrl(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lc-base">API tabanı (sadece anahtar kullanacaksanız)</Label>
              <Input
                id="lc-base"
                autoComplete="off"
                placeholder="http://yonetim.lastikcim.com.tr/api/products/turkishemarket"
                value={formApiBase}
                onChange={(e) => setFormApiBase(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lc-key">API anahtarı (isteğe bağlı — URL’de key yoksa)</Label>
              <Input
                id="lc-key"
                type="password"
                autoComplete="new-password"
                placeholder="Yalnızca yeni anahtar giriyorsanız doldurun"
                value={formApiKey}
                onChange={(e) => setFormApiKey(e.target.value)}
              />
            </div>

            <div className="space-y-2 border-t border-slate-200 pt-4">
              <Label htmlFor="lc-markup">Ek satış yüzdesi (%)</Label>
              <p className="text-xs text-slate-600">
                API’den gelen liste fiyatın üzerine bu kadar <strong>yüzde</strong> eklenir (
                örn: 10 → fiyat × 1,10). Kayıt ve Çalıştır sonrası ürün <code className="text-[11px]">price</code> /{' '}
                <code className="text-[11px]">base_price</code> buna göre güncellenir.
              </p>
              <div className="flex flex-wrap gap-2 items-center">
                <Input
                  id="lc-markup"
                  type="number"
                  min={0}
                  step="0.1"
                  autoComplete="off"
                  placeholder="0"
                  className="max-w-[160px]"
                  value={formMarkupPct}
                  onChange={(e) => setFormMarkupPct(e.target.value)}
                  disabled={loading}
                />
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={handlePreviewPricing}
                  disabled={loading || previewLoading || !status?.configured}
                >
                  {previewLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Önizleme (örnek ürün)
                </Button>
              </div>
              {pricingPreview?.ok && pricingPreview.sample && (
                <div className="mt-3 rounded-md border border-emerald-200 bg-emerald-50/70 p-3 text-sm text-slate-800 space-y-1">
                  <p className="font-semibold text-emerald-900">Örnek (API’den ilk geçerli satır)</p>
                  <p className="text-xs text-slate-600 tabular-nums">
                    Yüzde: <strong>{pricingPreview.markup_percent}</strong> —{' '}
                    {pricingPreview.sample.brand ? `${pricingPreview.sample.brand} · ` : ''}
                    <span>{pricingPreview.sample.product_name}</span>{' '}
                    <span className="text-slate-500">({pricingPreview.sample.code})</span>
                  </p>
                  <dl className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2 text-xs sm:text-sm">
                    <div>
                      <dt className="text-slate-600">API fiyatı (liste)</dt>
                      <dd className="font-mono font-medium">{formatTRY(pricingPreview.sample.api_price_try)}</dd>
                    </div>
                    <div>
                      <dt className="text-slate-600">Vitrin satış fiyatı</dt>
                      <dd className="font-mono font-medium text-emerald-800">{formatTRY(pricingPreview.sample.shelf_price_try)}</dd>
                    </div>
                    <div>
                      <dt className="text-slate-600">Önerilen üst çizgi (compare)</dt>
                      <dd className="font-mono font-medium">{formatTRY(pricingPreview.sample.compare_price_try)}</dd>
                    </div>
                  </dl>
                  <p className="text-[11px] text-slate-500 pt-1">
                    Eski = API liste; Yeni = liste × (1 + %{pricingPreview.markup_percent})
                  </p>
                </div>
              )}
              {!loading &&
                pricingPreview &&
                pricingPreview.ok === false &&
                (pricingPreview.message || pricingPreview.error) && (
                  <p className="text-xs text-amber-800 mt-1">{pricingPreview.message || pricingPreview.error}</p>
                )}
            </div>

            <Button type="button" onClick={handleSaveConfig} disabled={saving || loading}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Kaydet
            </Button>
          </div>

          {!status?.configured && !loading && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Ne <code>.env</code> ne de veritabanında geçerli bir adres yok. Yukarıya Lastikçim’in verdiği tam
                URL’yi yapıştırıp <strong>Kaydet</strong>’e basın (veya sunucuya ortam değişkeni ekleyin).
              </AlertDescription>
            </Alert>
          )}

          {status?.api_url_redacted && (
            <p className="text-sm text-slate-600 break-all">
              <span className="font-medium text-slate-800">Kullanılan uç (maskeli):</span>{' '}
              {status.api_url_redacted}
            </p>
          )}

          {lastRunResult && (
            <Alert className="border-emerald-200 bg-emerald-50">
              <CheckCircle2 className="h-4 w-4 text-emerald-700" />
              <AlertDescription className="text-emerald-900">
                Son çalıştırma: {lastRunResult.api_items} API satırı —{' '}
                <strong>{lastRunResult.imported}</strong> yeni, <strong>{lastRunResult.updated}</strong> güncelleme,
                atlanan <strong>{lastRunResult.skipped}</strong>.{' '}
                {typeof lastRunResult.price_markup_percent === 'number' && (
                  <>Uygulanan ek yüzde: <strong>%{lastRunResult.price_markup_percent}</strong>.</>
                )}{' '}
                Satıcı toplam ürün: <strong>{lastRunResult.total_seller_products}</strong>. Süre:{' '}
                {lastRunResult.duration_ms} ms.
              </AlertDescription>
            </Alert>
          )}

          <div>
            <h3 className="text-sm font-semibold text-slate-800 mb-2">Çalıştırma geçmişi</h3>
            <div className="rounded-md border bg-white overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tarih</TableHead>
                    <TableHead className="text-right">API satır</TableHead>
                    <TableHead className="text-right">Yeni</TableHead>
                    <TableHead className="text-right">Güncel</TableHead>
                    <TableHead className="text-right">Atlanan</TableHead>
                    <TableHead className="text-right">ms</TableHead>
                    <TableHead className="w-[100px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recent.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-slate-500 py-8">
                        Henüz kayıtlı çalıştırma yok. «Çalıştır» ile başlayın.
                      </TableCell>
                    </TableRow>
                  ) : (
                    recent.map((row) => {
                      const res = row.result || {};
                      const rid = row.id;
                      return (
                        <TableRow key={rid || row.created_at}>
                          <TableCell className="text-sm">
                            {row.created_at
                              ? new Date(row.created_at).toLocaleString('tr-TR')
                              : '—'}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">{res.api_items ?? '—'}</TableCell>
                          <TableCell className="text-right tabular-nums">{res.imported ?? '—'}</TableCell>
                          <TableCell className="text-right tabular-nums">{res.updated ?? '—'}</TableCell>
                          <TableCell className="text-right tabular-nums">{res.skipped ?? '—'}</TableCell>
                          <TableCell className="text-right tabular-nums">{res.duration_ms ?? '—'}</TableCell>
                          <TableCell>
                            <Button variant="ghost" size="sm" disabled={!rid} onClick={() => openDetail(rid)}>
                              <Eye className="w-4 h-4 mr-1" />
                              Detay
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Çalıştırma detayı</DialogTitle>
            <DialogDescription>Kayıt kimliği ve ham özet (örnek API satırları dahil).</DialogDescription>
          </DialogHeader>
          {detailLoading && (
            <div className="flex items-center justify-center py-12 text-slate-500">
              <Loader2 className="w-8 h-8 animate-spin mr-2" />
              Yükleniyor…
            </div>
          )}
          {!detailLoading && detailDoc && (
            <ScrollArea className="flex-1 max-h-[60vh] rounded-md border bg-slate-950 p-3">
              <pre className="text-xs text-slate-100 whitespace-pre-wrap break-words font-mono">
                {JSON.stringify(detailDoc, null, 2)}
              </pre>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IntegrationsTab;
